import { useEffect, useRef } from "react";
import { Form, Space } from "antd";

import { W1 } from "@components/Constants";
import { Stack } from "@components/Page";
import type { CreateScheduleInputMutation, UpdateScheduleInputMutation } from "@api/types";

import useDelete from "./helpers/useDelete";
import useFinish from "./helpers/useFinish";
import getNextDate from "./helpers/getNextDate";
import getFormFields from "./helpers/getFormFields";
import DistributionsScheduleSummary from "../../DistributionsScheduleSummary";
import type { Investment, ScheduleFormValues } from "../types";
import BeneficiariesTable, { type BeneficiariesTableHandle } from "../BeneficiariesTable";
import ScheduleName from "./ScheduleName";

type Props = {
  scheduleId?: string;
  investments: Investment[];
  initialValues: ScheduleFormValues;
  createSchedule: (mutation: CreateScheduleInputMutation) => Promise<unknown>;
  updateSchedule: (scheduleId: string, mutation: UpdateScheduleInputMutation) => Promise<unknown>;
  deleteSchedule: (scheduleId: string) => Promise<unknown>;
  onClose: () => void;
}


const DistributionsScheduleForm = ({
  scheduleId = undefined,
  onClose,
  investments,
  initialValues,
  createSchedule,
  updateSchedule,
  deleteSchedule,
}: Props) => {
  const [ form ] = Form.useForm<ScheduleFormValues>();

  const beneficiariesTableRef = useRef<BeneficiariesTableHandle>(null);

  const startDate = Form.useWatch("startDate", form);
  const shouldRoundCents = Form.useWatch("shouldRoundCents", form);
  const distributionFrequency = Form.useWatch("distributionFrequency", form);

  const nextDate = getNextDate(startDate, distributionFrequency);
  const { onFinish, isLoading } = useFinish({ beneficiariesTableRef, scheduleId, createSchedule, updateSchedule, onClose });
  const { onDeleteSchedule, isDeleting } = useDelete({ scheduleId, deleteSchedule, onClose });

  useEffect(() => {
    form.setFieldValue("nextDate", nextDate);
  }, [form, nextDate]);

  const formProps = {
    layout: "inline" as const,
    initialValues,
    form,
    onFinish,
  };

  const formFields = getFormFields({
    isUpdate: !!scheduleId,
    onDelete: onDeleteSchedule,
    isLoading,
    isDeleting,
  });

  const beneficiariesTableProps = {
    ref: beneficiariesTableRef,
    investments,
    shouldRoundCents: shouldRoundCents ?? initialValues.shouldRoundCents ?? false,
    initialDistributions: initialValues.distributions,
    distributionFrequency: distributionFrequency ?? initialValues.distributionFrequency ?? "MONTHLY",
  };

  return (
    <Stack>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Space size={W1}>
          <ScheduleName initialValues={initialValues} />
          <DistributionsScheduleSummary nextDate={nextDate} />
        </Space>

        <Form {...formProps}>
          {
            formFields.map(({ key, itemProps, children }) => (
              <Form.Item key={key} {...itemProps}>
                {children}
              </Form.Item>
            ))
          }
        </Form>
      </div>

      <BeneficiariesTable {...beneficiariesTableProps} />
    </Stack>
  );
};

export default DistributionsScheduleForm;
